import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, ViewChild, ViewChildren, DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from 'src/app/_constants/constants';
import { IArea } from 'src/app/_models/area.vm';
import { IIndicator } from 'src/app/_models/indicator.vm';
import { IPeriod } from 'src/app/_models/period.vm';
import { ISystemLevel } from 'src/app/_models/systemLevel.vm';
import { ApiService } from 'src/app/_services/api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trackByIndicatorId, trackByValue } from '../utils/track-by';
import { ErrorReportingService } from '../../_services/error-reporting.service';

/** Keeps the search field's id unique when more than one picker is on a page. */
let indicatorPickerCount = 0;

@Component({
    selector: 'app-select-indicator',
    templateUrl: './select-indicator.component.html',
    styleUrls: ['./select-indicator.component.scss'],
    standalone: false
})
export class SelectIndicatorComponent implements OnChanges {
  private readonly errorReporting = inject(ErrorReportingService);

  public readonly searchInputId: string = `cvdpIndicatorSearch${++indicatorPickerCount}`;

  public readonly trackByIndicatorId = trackByIndicatorId;
  public readonly trackByValue = trackByValue;

  // Ties every subscription below to this component's lifetime, so a response
  // that arrives after the user has navigated away is dropped instead of
  // writing into a view that no longer exists.
  private readonly destroyRef = inject(DestroyRef);


  @Input() public periodSelected: IPeriod;
  @Input() public indicatorSelected: IIndicator;
  @Input() public areaSelected?: IArea;
  @Input() public systemLevelSelected?: ISystemLevel;
  @Input() public showOutcomesIndicators?: boolean;
  @Input() public systemLevelTypeId?: number;
  /** Shown on the trigger before anything has been chosen. */
  @Input() public placeholder: string = 'Select an indicator...';
  /**
   * Which edge the panel hangs from. 'end' for a trigger in the last column of a
   * row, where a panel wider than its column would otherwise run off the page.
   */
  @Input() public panelAlign: 'start' | 'end' = 'start';
  @Output() public currentIndicator = new EventEmitter<IIndicator>();

  public indicators: Array<IIndicator>;
  public rawIndicators: Array<IIndicator>;

  public indicatorSelectorVisible: boolean = false;

  public indicatorDataLoadingStatus: string = null;

  public conditions: any = Constants.conditions;
  public outcomesConditions: any = Constants.OutcomesConditions;
  public conditionSelected: any;

  public missingHypIndicators: boolean = false;
  public missing004CKDIndicator: boolean = false;
  public missingCVDP002ADMNIndicator: boolean;

  /**
   * Free text search across the whole indicator list for the period, not just the
   * condition on show. Someone who knows the code they want should not have to work
   * out which condition holds it first.
   */
  public searchTerm: string = '';
  /** Row the keyboard is on, so Enter can pick it without reaching for the mouse. */
  public activeIndex: number = -1;

  @ViewChild('searchInput') private searchInput: ElementRef<HTMLInputElement>;
  @ViewChildren('indicatorButton') private indicatorButtons: QueryList<ElementRef<HTMLElement>>;

  /**
   * The period and system level the list in hand was fetched for. ngOnChanges also
   * fires when the chosen indicator is fed back in, and refetching then would throw
   * away whatever the user had typed.
   */
  private loadedFor: string = null;

  constructor(
    private apiService: ApiService,
    private router: Router,
  ) { }

  ngOnChanges() {
    if (this.router.url.split('?')[0] == '/outcomes' || this.showOutcomesIndicators) {
      this.conditions = this.outcomesConditions;
    } else {
      this.conditions = Constants.conditions;
    }
    const systemLevelId = this.systemLevelSelected ? this.systemLevelSelected.SystemLevelID : this.areaSelected.SystemLevelID;
    const key = `${this.periodSelected.TimePeriodID}:${systemLevelId}:${!!this.showOutcomesIndicators}`;
    if (key === this.loadedFor) {
      return;
    }
    this.loadedFor = key;
    this.getIndicators(this.periodSelected.TimePeriodID, systemLevelId);
  }

  // Opening and closing -----------------------------------------------------

  public toggleDropdown(): void {
    if (this.indicatorSelectorVisible) {
      this.closeDropdown();
      return;
    }
    this.indicatorSelectorVisible = true;
    this.activeIndex = -1;
    // The field is only in the DOM once the panel has rendered.
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  closeDropdown() {
    this.indicatorSelectorVisible = false;
    // A term left behind would silently narrow the list the next time the panel is
    // opened, so each visit starts from the condition on show.
    if (this.searchTerm) {
      this.searchTerm = '';
      this.applyFilters();
    }
    this.activeIndex = -1;
  }

  // Searching and filtering -------------------------------------------------

  public onSearchChange(term: string): void {
    this.searchTerm = term;
    this.applyFilters();
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.applyFilters();
    this.searchInput?.nativeElement.focus();
  }

  public get searching(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  /** Fed to the highlight pipe, which builds a regular expression from it. */
  public get searchPattern(): string {
    return this.searchTerm.trim();
  }

  private matchesSearch(indicator: IIndicator, term: string): boolean {
    return [indicator.IndicatorShortName, indicator.IndicatorName, indicator.IndicatorCode]
      .some(field => (field || '').toLowerCase().includes(term));
  }

  private indicatorsForCondition(condition: any): Array<IIndicator> {
    if (!condition) {
      return this.rawIndicators || [];
    }
    return (this.rawIndicators || []).filter(value => condition.codes.some(code => value.IndicatorCode.slice(-5).includes(code)));
  }

  private applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.indicators = term
      ? (this.rawIndicators || []).filter(indicator => this.matchesSearch(indicator, term))
      : this.indicatorsForCondition(this.conditionSelected);
    this.activeIndex = this.indicators.length ? 0 : -1;
  }

  // Keyboard ----------------------------------------------------------------

  public onSearchKeydown(event: KeyboardEvent): void {
    if (event.key == 'ArrowDown') {
      event.preventDefault();
      this.moveActive(1);
      return;
    }
    if (event.key == 'ArrowUp') {
      event.preventDefault();
      this.moveActive(-1);
      return;
    }
    if (event.key == 'Enter') {
      event.preventDefault();
      const indicator = this.indicators ? this.indicators[this.activeIndex] : null;
      if (indicator) {
        this.setIndicator(indicator.IndicatorID);
      }
      return;
    }
    if (event.key == 'Escape') {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  private moveActive(step: number): void {
    const count = this.indicators ? this.indicators.length : 0;
    if (!count) {
      this.activeIndex = -1;
      return;
    }
    this.activeIndex = (this.activeIndex + step + count) % count;
    // The rows are already in the DOM, so this only has to bring one into view.
    const row = this.indicatorButtons ? this.indicatorButtons.get(this.activeIndex) : null;
    if (row) {
      row.nativeElement.scrollIntoView({ block: 'nearest' });
    }
  }

  // Data --------------------------------------------------------------------

  getIndicators(periodId: number, systemLevelId: number): void {
    this.indicatorDataLoadingStatus = 'Loading indicators...';
    this.apiService.getIndicatorList(periodId, systemLevelId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.indicatorDataLoadingStatus = null;
        this.rawIndicators = success.indicatorList;
        // Keep the condition the user was on if this period still offers it,
        // otherwise fall back to the first.
        const stillOffered = this.conditionSelected && this.conditions.some(condition => condition.id == this.conditionSelected.id);
        this.filterByCondition(stillOffered ? this.conditionSelected : this.conditions[0]);
      },
      error => {
        // Without this the "Loading indicators..." message stayed up for good and
        // the page looked like it was still working.
        this.indicatorDataLoadingStatus = 'There has been an error retrieving the data. Please reload the page.';
        this.errorReporting.report('Loading the indicator list', error);
      }
    )
  }

  setIndicator(indicatorId: number): void {
    this.indicatorDataLoadingStatus = 'Loading indicators...';
    if (this.systemLevelSelected) {
      // Searching can put an indicator on screen that the condition filter would
      // have hidden, so the lookup runs over the full list rather than the visible one.
      this.indicatorSelected = this.rawIndicators.find(ind => ind.IndicatorID == indicatorId);
      this.currentIndicator.emit(this.indicatorSelected);
      this.closeDropdown();
      this.indicatorDataLoadingStatus = null;
    } else {
      this.getIndicator(indicatorId)
    }
  }

  getIndicator(indicatorId: number): void {
    this.indicatorSelected = null;
    this.closeDropdown();
    this.apiService.getIndicator(indicatorId, this.periodSelected.TimePeriodID, this.areaSelected.AreaID, this.systemLevelTypeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.indicatorSelected = success.indicatorData;
        this.currentIndicator.emit(this.indicatorSelected);
      },
      error => {
        this.indicatorDataLoadingStatus = 'There has been an error retrieving the data. Please reload the page.';
        this.errorReporting.report('Get indicator', error);
      },
      () => {
        this.indicatorDataLoadingStatus = null;
      }
    )
  }

  filterByCondition(condition: any): void {
    this.conditionSelected = condition;
    // Choosing a condition is a deliberate move away from whatever was searched for.
    this.searchTerm = '';
    this.applyFilters();

    // Missing HYP Indicators, To September 2021
    if (this.periodSelected.TimePeriodID == 3 && condition.id == 'HYP') {
      this.missingHypIndicators = true;
    } else {
      this.missingHypIndicators = false;
    }
    // Missing CVD0004CKD Indicator (temporary)
    if (condition.id == 'CKD') {
      this.missing004CKDIndicator = true;
    } else {
      this.missing004CKDIndicator = false;
    }
    // areaSelected is absent on the data extract page, which picks a system level
    // rather than an area.
    if (condition.id == 'ADMN' && this.areaSelected?.AreaID == 8069) {
      this.missingCVDP002ADMNIndicator = true;
    } else {
      this.missingCVDP002ADMNIndicator = false;
    }
  }

}
